import { useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Customer } from '@cmms/api-client'
import type { Site, Role, BulkCreateUserRow } from '@cmms/types'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

/// Bulk user import via CSV. Parses + validates client-side so every
/// problem surfaces in one pass; the server-side bulk endpoint is the
/// final guard for things this can't check (duplicate emails across
/// the org, role-scope rule violations).
///
/// Expected CSV columns (header row required):
///   name, email, password, role, phone, customers, sites
///
/// `customers` / `sites` are semicolon-separated NAMES that we resolve
/// to ids client-side using the org's current customer/site list. A
/// CSV exporter that already has the UUIDs can pass them directly.
export function UsersImportDialog({
  open, onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [parsedRows, setParsedRows] = useState<BulkCreateUserRow[]>([])
  const [parseErrors, setParseErrors] = useState<ParseError[]>([])
  const [serverErrors, setServerErrors] = useState<Array<{ row: number; name: string | null; message: string }>>([])
  // Help panel is expanded by default the first time the dialog opens
  // (admins need the guide most then); admins can collapse it once
  // they're familiar.
  const [helpOpen, setHelpOpen] = useState(true)

  // Customer + site + role catalogs — used to resolve names in the CSV
  // to ids. Loaded on dialog open.
  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    enabled: open,
  })
  const sitesQ = useQuery({
    queryKey: ['sites', 'all-customers'],
    queryFn: () => api.sites.list({ allCustomers: true }),
    enabled: open,
  })
  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.roles.list(),
    enabled: open,
  })

  const customers: Customer[] = Array.isArray(customersQ.data) ? customersQ.data : []
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const roles: Role[] = Array.isArray(rolesQ.data) ? rolesQ.data : []

  const importM = useMutation({
    mutationFn: () => api.users.bulkCreate(parsedRows),
    onSuccess: (result) => {
      toast.success(`Imported ${result.created} user${result.created === 1 ? '' : 's'}`)
      qc.invalidateQueries({ queryKey: ['users'] })
      reset()
      onClose()
    },
    onError: (e) => {
      // The backend returns { errors: [{row, name, message}] } for
      // per-row failures. Surface those inline so the admin can fix
      // the offending lines without losing the rest of the upload.
      if (e instanceof ApiError && (e as any).data?.errors) {
        setServerErrors((e as any).data.errors)
      } else {
        toast.error(e instanceof ApiError ? e.message : 'Import failed')
      }
    },
  })

  function reset() {
    setFileName(null)
    setParsedRows([])
    setParseErrors([])
    setServerErrors([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleFile(file: File) {
    setServerErrors([])
    const text = await file.text()
    const { rows, errors } = parseCsv(text, { customers, sites, roles })
    setFileName(file.name)
    setParsedRows(rows)
    setParseErrors(errors)
  }

  function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'users_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const validRowCount = parsedRows.length
  const totalIssues = parseErrors.length + serverErrors.length

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>
        Import users from CSV
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
          Each row creates a user with the credentials you specify — same as the Create user dialog,
          just in bulk. The import is all-or-nothing: if any row is invalid, no users are created
          and you can fix the file and re-run.
        </Typography>

        {/* Help / guide panel — collapsed into a single block so the
            admin can see the schema, valid roles, and the exact
            customer / site names they need to type into the CSV. The
            values come from this org's live data so the names match
            byte-for-byte. */}
        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
          mb: 2, overflow: 'hidden',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
        }}>
          <Box onClick={() => setHelpOpen((v) => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 1, cursor: 'pointer',
              '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)' },
            }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
              How to build the CSV
            </Typography>
            {helpOpen
              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          {helpOpen && (
            <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {/* ── Schema reference ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                    Columns
                  </Typography>
                  <Stack spacing={0.5} sx={{ fontSize: 12 }}>
                    <Typography sx={{ fontSize: 12 }}>
                      <Box component="code" sx={{
                        bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                      }}>name</Box>
                      <Box component="code" sx={{
                        bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                      }}>email</Box>
                      <Box component="code" sx={{
                        bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                      }}>password</Box>
                      <Box component="code" sx={{
                        bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700,
                      }}>role</Box>
                      <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.5 }}>
                        required
                      </Typography>
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      <Box component="code" sx={{
                        bgcolor: 'action.hover', color: 'text.secondary',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                      }}>phone</Box>
                      <Box component="code" sx={{
                        bgcolor: 'action.hover', color: 'text.secondary',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                      }}>customers</Box>
                      <Box component="code" sx={{
                        bgcolor: 'action.hover', color: 'text.secondary',
                        px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700,
                      }}>sites</Box>
                      <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.5 }}>
                        optional
                      </Typography>
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1.5, lineHeight: 1.55 }}>
                    Put multiple values in <Box component="code" sx={{ bgcolor: 'action.hover',
                      px: 0.5, borderRadius: 0.5, fontSize: 11 }}>customers</Box> or
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5,
                      borderRadius: 0.5, fontSize: 11, ml: 0.5 }}>sites</Box> by separating
                    with a semicolon — e.g. <Box component="code" sx={{ bgcolor: 'action.hover',
                      px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Site A;Site B</Box>.
                    Passwords need 8+ characters. Non-admin roles require at least one customer.
                  </Typography>
                </Box>

                {/* ── Valid values: roles + customer names + site names ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                    Valid values (from your org)
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                        Roles
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {roles.length === 0
                          ? <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>Loading…</Typography>
                          : roles.map((r) => (
                              <Chip key={r.id} label={r.name} size="small"
                                sx={{ height: 20, fontSize: 11, fontWeight: 600,
                                  bgcolor: (t) => `${t.palette.primary.main}14`,
                                  color: 'primary.main' }} />
                            ))}
                      </Stack>
                    </Box>
                    {customers.length > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                          Customer names (copy these exactly)
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {customers.map((c) => (
                            <CopyChip key={c.id} text={c.name} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {sites.length > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                          Site names <Typography component="span" sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                            (showing {Math.min(sites.length, 8)} of {sites.length})
                          </Typography>
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {sites.slice(0, 8).map((s) => (
                            <CopyChip key={s.id} text={s.name} />
                          ))}
                          {sites.length > 8 && (
                            <Typography sx={{ fontSize: 10.5, color: 'text.disabled', alignSelf: 'center' }}>
                              +{sites.length - 8} more
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>

              {/* Inline example row — shows the exact shape, complete
                  with semicolon separators. */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                  Example row
                </Typography>
                <Box sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  p: 1.25, bgcolor: 'background.paper',
                  fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: 'text.primary',
                }}>
                  {'name,email,password,role,phone,customers,sites'}
                  {'\n'}
                  {`Ravi Kumar,ravi@example.com,Welcome123!,${roles[0]?.name ?? 'technician'},+91-9876543210,${customers[0]?.name ?? 'Acme Inc'}${customers.length > 1 ? `;${customers[1]!.name}` : ''},${sites[0]?.name ?? 'Site A'}`}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Template + file picker row */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={downloadTemplate}
            sx={{ fontSize: 12, color: 'text.secondary', borderColor: 'divider' }}>
            Download template
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleFile(f)
            }} />
          <Button size="small" variant="contained" startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ fontSize: 12, fontWeight: 600 }}>
            {fileName ? 'Choose different file' : 'Pick CSV file'}
          </Button>
          {fileName && (
            <>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{fileName}</Typography>
              </Stack>
              <Tooltip title="Reset" arrow>
                <IconButton size="small" onClick={reset} sx={{ color: 'text.secondary' }}>
                  <RestartAltOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>

        {/* Summary chips */}
        {fileName && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
              label={`${validRowCount} ready to import`}
              size="small"
              sx={{
                height: 24, fontSize: 11.5, fontWeight: 600,
                bgcolor: '#05966918', color: '#059669',
                '& .MuiChip-icon': { color: '#059669' },
              }} />
            {totalIssues > 0 && (
              <Chip
                icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                label={`${totalIssues} issue${totalIssues === 1 ? '' : 's'}`}
                size="small"
                sx={{
                  height: 24, fontSize: 11.5, fontWeight: 600,
                  bgcolor: '#DC262618', color: '#DC2626',
                  '& .MuiChip-icon': { color: '#DC2626' },
                }} />
            )}
          </Stack>
        )}

        {/* Error list */}
        {(parseErrors.length > 0 || serverErrors.length > 0) && (
          <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>
              Fix these and re-upload:
            </Typography>
            <Stack spacing={0.5}>
              {[...parseErrors, ...serverErrors].slice(0, 50).map((err, i) => (
                <Typography key={i} sx={{ fontSize: 12 }}>
                  Row {err.row}{err.name ? ` (${err.name})` : ''} — {err.message}
                </Typography>
              ))}
              {parseErrors.length + serverErrors.length > 50 && (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  …and {parseErrors.length + serverErrors.length - 50} more
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {/* Preview table */}
        {parsedRows.length > 0 && (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
            maxHeight: 320, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>Email</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>Role</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>Customers</TableCell>
                  <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>Sites</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedRows.slice(0, 100).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: 12 }}>{r.name}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.email}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.role}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {r.customer_ids?.length ?? 0}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {r.site_ids?.length ?? 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {parsedRows.length > 100 && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'center', py: 0.75 }}>
                Showing first 100 of {parsedRows.length} rows
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={importM.isPending}
          sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained"
          disabled={parsedRows.length === 0 || parseErrors.length > 0 || importM.isPending}
          onClick={() => importM.mutate()}
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {importM.isPending
            ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
            : `Import ${validRowCount} user${validRowCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── CSV parsing ────────────────────────────────────────────────────────

interface ParseError {
  row: number
  name: string | null
  message: string
}

const CSV_TEMPLATE =
  'name,email,password,role,phone,customers,sites\n' +
  'Ravi Kumar,ravi@example.com,Welcome123!,technician,+91-9876543210,Bacsys Innovations,Site A;Site B\n' +
  'Priya Singh,priya@example.com,SecurePass!,viewer,,Bacsys Innovations,\n'

function parseCsv(
  text: string,
  ctx: { customers: Array<{ id: string; name: string }>; sites: Array<{ id: string; name: string; customer_id?: string | null }>; roles: Array<{ name: string }> },
): { rows: BulkCreateUserRow[]; errors: ParseError[] } {
  const lines = text.replace(/\r\n/g, '\n').trim().split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length < 2) {
    return { rows: [], errors: [{ row: 1, name: null, message: 'CSV must have a header row and at least one data row.' }] }
  }

  const header = lines[0]!.split(',').map((h) =>
    h.trim().toLowerCase().replace(/^"/, '').replace(/"$/, ''))
  const idx = (col: string) => header.indexOf(col)

  const errors: ParseError[] = []
  const required = ['name', 'email', 'password', 'role']
  for (const col of required) {
    if (idx(col) < 0) {
      errors.push({ row: 1, name: null, message: `Missing required column "${col}".` })
    }
  }
  if (errors.length > 0) return { rows: [], errors }

  const nameIdx     = idx('name')
  const emailIdx    = idx('email')
  const passwordIdx = idx('password')
  const roleIdx     = idx('role')
  const phoneIdx    = idx('phone')
  const custIdx     = idx('customers')
  const siteIdx     = idx('sites')

  const customerByName = new Map(ctx.customers.map((c) => [c.name.toLowerCase(), c.id]))
  const siteByName     = new Map(ctx.sites.map((s) => [s.name.toLowerCase(), s]))
  const validRoles     = new Set(ctx.roles.map((r) => r.name))

  const rows: BulkCreateUserRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]!)
    const get  = (j: number) => (j < 0 ? '' : (cols[j] ?? '').trim())
    const rowNum = i + 1

    const name     = get(nameIdx)
    const email    = get(emailIdx)
    const password = get(passwordIdx)
    const role     = get(roleIdx).toLowerCase()
    const phone    = get(phoneIdx)
    const custList = get(custIdx)
    const siteList = get(siteIdx)

    if (!name)     { errors.push({ row: rowNum, name: null, message: 'Name is required.' }); continue }
    if (!email)    { errors.push({ row: rowNum, name, message: 'Email is required.' }); continue }
    if (!email.includes('@')) { errors.push({ row: rowNum, name, message: `"${email}" is not a valid email.` }); continue }
    if (!password) { errors.push({ row: rowNum, name, message: 'Password is required.' }); continue }
    if (password.length < 8) { errors.push({ row: rowNum, name, message: 'Password must be at least 8 characters.' }); continue }
    if (!role)     { errors.push({ row: rowNum, name, message: 'Role is required.' }); continue }
    if (!validRoles.has(role)) {
      errors.push({ row: rowNum, name,
        message: `Unknown role "${role}". Available: ${Array.from(validRoles).join(', ')}` })
      continue
    }

    let customer_ids: string[] | undefined
    if (custList) {
      const names = custList.split(';').map((n) => n.trim()).filter(Boolean)
      const ids: string[] = []
      const unknown: string[] = []
      for (const n of names) {
        const id = customerByName.get(n.toLowerCase())
        if (id) ids.push(id)
        else unknown.push(n)
      }
      if (unknown.length > 0) {
        errors.push({ row: rowNum, name,
          message: `Unknown customer${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}` })
        continue
      }
      customer_ids = ids
    }

    let site_ids: string[] | undefined
    if (siteList) {
      const names = siteList.split(';').map((n) => n.trim()).filter(Boolean)
      const ids: string[] = []
      const unknown: string[] = []
      for (const n of names) {
        const s = siteByName.get(n.toLowerCase())
        if (s) ids.push(s.id)
        else unknown.push(n)
      }
      if (unknown.length > 0) {
        errors.push({ row: rowNum, name,
          message: `Unknown site${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}` })
        continue
      }
      site_ids = ids
    }

    // Mirror the backend rule so the admin sees scope problems before
    // the round-trip: non-admin roles need at least one customer.
    if (role !== 'org_admin' && (!customer_ids || customer_ids.length === 0)) {
      errors.push({ row: rowNum, name,
        message: `Role "${role}" needs at least one customer in the "customers" column.` })
      continue
    }

    rows.push({
      name, email, password, role, phone: phone || undefined,
      customer_ids, site_ids,
    })
  }

  return { rows, errors }
}

/// Minimal CSV-line parser supporting quoted values + escaped quotes.
/// Lifted from AssetsImportDialog to keep the import flow self-contained.
function parseCsvLine(line: string): string[] {
  const cols: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++ }
      else inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      cols.push(current); current = ''
    } else {
      current += ch
    }
  }
  cols.push(current)
  return cols
}

/// Small clickable chip that copies its label to the clipboard. Used
/// in the help panel so admins can grab exact customer/site names
/// without retyping (typos here would land in the CSV and fail the
/// import).
function CopyChip({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Tooltip title={copied ? 'Copied!' : 'Click to copy'} arrow>
      <Chip
        label={text}
        size="small"
        icon={<ContentCopyOutlinedIcon sx={{ fontSize: 11, color: 'inherit !important' }} />}
        onClick={() => {
          navigator.clipboard?.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1200)
          })
        }}
        sx={{
          height: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
          bgcolor: copied ? '#05966918' : 'action.hover',
          color: copied ? '#059669' : 'text.primary',
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
          '&:hover': { bgcolor: copied ? '#05966926' : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)' },
        }}
      />
    </Tooltip>
  )
}
